// src/components/CarrierApplyModal.jsx
import React, { useEffect, useState } from "react";
import api from "../../../configs/api";
import { showSuccess, showError } from "../../../utils/toastUtils";

const CarrierInquiryModal = ({ isOpen, onClose, carrierId }) => {
  const [carrier, setCarrier] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    coverLetter: "",
    resume: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (carrierId) fetchCarrier();
  }, [carrierId]);

  const fetchCarrier = async () => {
    try {
      setCarrierLoading(true);
      const res = await api.get(`/carriers/${carrierId}`);

      setCarrier(res.data?.data);
      console.log("response", res);
    } catch (err) {
      console.error(err);
    } finally {
      setCarrierLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "resume") {
      setForm({ ...form, resume: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/auto/upload`,
      {
        method: "POST",
        body: data,
      },
    );

    const result = await res.json();
    return result.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      showError("Please enter a valid email address");
      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(form.phone)) {
      showError("Phone number must be exactly 10 digits");
      return;
    }

    try {
      setLoading(true);

      let resumeUrl = "";
      if (form.resume) {
        resumeUrl = await uploadToCloudinary(form.resume);
      }

      await api.post("/carrier-inquiries/", {
        ...form,
        resume: resumeUrl,
        carrier: carrierId,
      });

      showSuccess("Application submitted successfully!");

      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        coverLetter: "",
        resume: null,
      });

      onClose();
    } catch (err) {
      console.error(err);

      showError("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-3 sm:px-4 py-6 overflow-y-auto">
      <div
        className="bg-white w-full max-w-6xl rounded-2xl sm:rounded-3xl shadow-2xl 
               grid grid-cols-1 md:grid-cols-2 max-h-[95vh] overflow-hidden"
      >
        {/* LEFT SIDE → JOB DETAILS */}
        <div className="hidden md:block bg-gray-50 p-5 sm:p-6 md:p-8 border-b md:border-b-0 md:border-r overflow-y-auto">
          {carrierLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-gray-300 rounded w-2/3"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                {carrier?.name || "Job Role"}
              </h2>

              <p className="text-sm text-gray-500 mb-6">
                📍 {carrier?.location || "Location"}
              </p>

              <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                <div>
                  <p className="text-gray-500">Experience</p>
                  <p className="font-medium">{carrier?.experience || "-"}</p>
                </div>

                <div>
                  <p className="text-gray-500">Salary</p>
                  <p className="font-medium">{carrier?.salary || "-"}</p>
                </div>

                <div>
                  <p className="text-gray-500">Job Type</p>
                  <p className="font-medium">{carrier?.time || "-"}</p>
                </div>

                <div>
                  <p className="text-gray-500">Company</p>
                  <p className="font-medium">MixMaster RMC</p>
                </div>
              </div>

              <div className="mt-6 sm:mt-8">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Job Description
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {carrier?.description || "No description available"}
                </p>
              </div>
            </>
          )}
        </div>

        {/* RIGHT SIDE → FORM */}
        <div className="p-5 sm:p-6 md:p-8 relative overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-4 sm:top-4 sm:right-5 text-gray-400 hover:text-black text-lg sm:text-xl"
          >
            ✕
          </button>

          <h3 className="text-lg sm:text-xl font-semibold mb-5 sm:mb-6">
            Apply Now
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Inputs */}
            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              required
              value={form.name}
              onChange={handleChange}
              className="w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 outline-none"
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              required
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 outline-none"
            />

            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              required
              value={form.phone}
              onChange={handleChange}
              className="w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 outline-none"
            />

            <textarea
              name="coverLetter"
              placeholder="Write a short cover letter..."
              rows={4}
              value={form.coverLetter}
              onChange={handleChange}
              className="w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 outline-none"
            />

            {/* Resume Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Upload Resume *
              </label>

              <div
                onClick={() => document.getElementById("resumeInput").click()}
                className="border-2 border-dashed rounded-xl p-5 sm:p-6 text-center cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition"
              >
                <p className="text-sm text-gray-500">
                  Click to upload or drag & drop
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PDF, DOC, DOCX (Max 5MB)
                </p>

                {/* Hidden Input */}
                <input
                  id="resumeInput"
                  type="file"
                  name="resume"
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                  className="hidden"
                />

                {/* Preview */}
                {form.resume && (
                  <div className="mt-3 text-green-600 text-xs font-medium">
                    {form.resume.name}
                  </div>
                )}
              </div>
            </div>
            {/* Submit */}
            <div className="sticky bottom-0 bg-white pt-3 pb-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-btnColor hover:bg-btnHoverColor transition text-white py-3 rounded-xl font-medium shadow-md"
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CarrierInquiryModal;
